export type WhatsAppTemplatePresetKey =
  | "confirmation"
  | "reminder"
  | "fallback"
  | "cart_recovery"
  | "order_confirmed"
  | "dropi_guia_generada"
  | "dropi_en_camino"
  | "dropi_entregado"
  | "dropi_devuelto"
  | "dropi_cancelado";

export type WhatsAppTemplateComponent = {
  type?: string;
  text?: string;
  format?: string;
  buttons?: Array<{ type?: string; text?: string | null }>;
};

export interface WhatsAppTemplatePresetSpec {
  displayName: string;
  variables: number;
  buttons: boolean;
  headerFormat?: "IMAGE";
  suggestedName: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION";
  variableLabels: string[];
  recommendedBody: string;
}

export const WHATSAPP_TEMPLATE_PRESET_KEYS: WhatsAppTemplatePresetKey[] = [
  "confirmation",
  "reminder",
  "fallback",
  "cart_recovery",
  "order_confirmed",
  "dropi_guia_generada",
  "dropi_en_camino",
  "dropi_entregado",
  "dropi_devuelto",
  "dropi_cancelado",
];

// Paquete oficial del chatbot (PLANTILLAS_CHATBOT_PAQUETE_V1.0): copys universales y claros,
// la confirmación SIN botones (inicia la conversación y el chatbot toma el flujo) y sin
// prometer nada no configurado (tiempos de entrega, etc.). Variables alineadas al orden
// exacto que envía el backend.
export const WHATSAPP_TEMPLATE_PRESETS: Record<WhatsAppTemplatePresetKey, WhatsAppTemplatePresetSpec> = {
  confirmation: {
    displayName: "Confirmación de pedido (inicia la conversación)",
    variables: 4,
    buttons: false,
    suggestedName: "chatbot_confirmacion_inicio_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Productos del pedido", "Total", "Dirección completa"],
    recommendedBody:
      "Hola {{1}} 👋 Recibimos tu pedido de {{2}} por {{3}}.\n\n" +
      "Antes de despacharlo quiero confirmar contigo los datos de entrega: {{4}}.\n\n" +
      "¿Me confirmas que todo está correcto o deseas ajustar algo? Estoy aquí para ayudarte.",
  },
  reminder: {
    displayName: "Recordatorio de confirmación",
    variables: 4,
    buttons: false,
    suggestedName: "chatbot_confirmacion_recordatorio_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Productos", "Total"],
    recommendedBody:
      "Hola {{1}}, aún tenemos pendiente confirmar tu pedido {{2}}: {{3}} por {{4}}.\n\n" +
      "¿Deseas que lo dejemos listo para el despacho? Respóndeme por aquí y lo resolvemos en un momento.",
  },
  fallback: {
    displayName: "Solicitud de dirección (en desuso)",
    variables: 5,
    buttons: false,
    suggestedName: "order_direccion_fluxi",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Producto", "Cantidad (unidades)", "Total"],
    recommendedBody:
      "Hola {{1}}, recibimos tu pedido #{{2}}:\n\n" +
      "📦 Producto: {{3}}\n" +
      "📦 Unidades: {{4}}\n" +
      "💰 Total: ${{5}}\n\n" +
      "Nos falta tu dirección de envío. ¿Podrías compartirla? Envíanos: dirección, ciudad y departamento.",
  },
  cart_recovery: {
    displayName: "Recuperación de carrito",
    variables: 4,
    buttons: false,
    suggestedName: "chatbot_carrito_recuperacion_v1",
    category: "MARKETING",
    variableLabels: ["Nombre del cliente", "Producto", "Cantidad (unidades)", "Total"],
    recommendedBody:
      "Hola {{1}} 👋 Vimos que dejaste tu compra de {{2}} ({{3}} unidad(es)) sin terminar.\n\n" +
      "Sigue disponible por {{4}}. Si quieres, la retomamos por aquí: dime y te ayudo a completarla en un par de minutos.",
  },
  order_confirmed: {
    displayName: "Pedido confirmado",
    variables: 0,
    buttons: false,
    suggestedName: "chatbot_pedido_confirmado_v1",
    category: "UTILITY",
    variableLabels: [],
    recommendedBody:
      "¡Listo! 🎉 Tu pedido quedó confirmado.\n\n" +
      "Estamos preparando el despacho y, cuando tengamos la guía de envío, te la compartimos por este mismo chat para que puedas hacerle seguimiento.\n\n" +
      "¡Gracias por tu compra!",
  },
  dropi_guia_generada: {
    displayName: "Envíos: guía generada",
    variables: 3,
    buttons: false,
    suggestedName: "chatbot_guia_generada_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Número de guía"],
    recommendedBody:
      "Hola {{1}}, ¡tu pedido {{2}} ya está listo para salir! 📦\n\n" +
      "Guía de seguimiento: {{3}}\n\n" +
      "Te seguimos avisando por aquí a medida que avance.",
  },
  dropi_en_camino: {
    displayName: "Envíos: pedido en camino",
    variables: 3,
    buttons: false,
    suggestedName: "chatbot_en_camino_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Número de guía"],
    recommendedBody:
      "Hola {{1}}, tu pedido {{2}} está en ruta de entrega 🚚\n\n" +
      "Guía: {{3}}\n\n" +
      "Te recomendamos estar pendiente del teléfono por si la transportadora te contacta. Si tu pago es contra entrega, ten el valor a la mano.",
  },
  dropi_entregado: {
    displayName: "Envíos: pedido entregado",
    variables: 3,
    buttons: false,
    suggestedName: "chatbot_entregado_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Mensaje"],
    recommendedBody:
      "Hola {{1}}, tu pedido {{2}} figura como entregado ✅\n\n" +
      "{{3}}\n\n" +
      "¡Gracias por tu compra! Si algo no llegó como esperabas, escríbeme por aquí y lo revisamos.",
  },
  dropi_devuelto: {
    displayName: "Envíos: pedido devuelto",
    variables: 3,
    buttons: false,
    suggestedName: "chatbot_devuelto_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Motivo"],
    recommendedBody:
      "Hola {{1}}, tu pedido {{2}} fue devuelto por la transportadora.\n\n" +
      "Motivo: {{3}}\n\n" +
      "¿Quieres que lo intentemos de nuevo o revisamos los datos de entrega? Escríbeme y lo solucionamos.",
  },
  dropi_cancelado: {
    displayName: "Envíos: envío cancelado",
    variables: 3,
    buttons: false,
    suggestedName: "chatbot_envio_cancelado_v1",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Motivo"],
    recommendedBody:
      "Hola {{1}}, el envío de tu pedido {{2}} quedó cancelado.\n\n" +
      "Motivo: {{3}}\n\n" +
      "Si no lo pediste tú o quieres retomarlo, escríbeme por aquí y lo miramos juntos.",
  },
};

export const CORE_WHATSAPP_TEMPLATE_PRESET_KEYS: WhatsAppTemplatePresetKey[] = [
  "confirmation",
  "reminder",
  "cart_recovery",
  "order_confirmed",
];

export const buildWhatsAppTemplateComponents = (
  body: string,
  withButtons: boolean,
  headerFormat?: "IMAGE"
): WhatsAppTemplateComponent[] => {
  const components: WhatsAppTemplateComponent[] = [];
  if (headerFormat === "IMAGE") {
    components.push({ type: "HEADER", format: "IMAGE" });
  }
  components.push({ type: "BODY", text: body });
  if (withButtons) {
    components.push({
      type: "BUTTONS",
      buttons: [
        { type: "QUICK_REPLY", text: "Confirmar Pedido" },
        { type: "QUICK_REPLY", text: "Modificar Datos" },
      ],
    });
  }
  return components;
};

export const listMissingWhatsAppTemplatePresets = (
  templates: Array<{ name?: string | null; language?: string | null }>,
  presetKeys: WhatsAppTemplatePresetKey[] = CORE_WHATSAPP_TEMPLATE_PRESET_KEYS
) =>
  presetKeys.filter((key) => {
    const spec = WHATSAPP_TEMPLATE_PRESETS[key];
    return !templates.some((template) => template.name === spec.suggestedName && (template.language || "es") === "es");
  });
